import { Button } from '@/components/ui/button';
import { RaffleSubmissionData } from '@/generated/graphql';
import { useAuth } from '@/hooks/useAuth';
import { useCreateSubmission } from '@/hooks/useCreateSubmission';
import { TypedSubmission } from '@/utils/engagementTypeUtils';
import { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { RaffleTicket } from '../RaffleTicket';
import { cn } from '@/lib/utils';
import { hasReasonableSignatureStrokes } from './signatureAmount';
import {
  canvasToPngBlob,
  renderSignaturePointsToCanvas,
  SignaturePointGroup,
} from '../signatureExport';

const FLIP_WAIT_TIME = 2_500;
const FLIGHT_WAIT_TIME = 500;

function hasReasonableSignature(pad: SignatureCanvas | null): boolean {
  if (!pad) return false;
  return hasReasonableSignatureStrokes(pad.toData());
}

export function RaffleTicketForm({
  engagementId,
  className,
  onSubmitted,
  skipIntro = false,
}: {
  engagementId: string;
  className?: string;
  onSubmitted: (submission: TypedSubmission<RaffleSubmissionData>) => void;
  skipIntro?: boolean;
}) {
  const { user } = useAuth();
  const [hasFlownIn, setHasFlownIn] = useState(skipIntro);
  const [showSignWell, setShowSignWell] = useState(skipIntro);
  const [padSize, setPadSize] = useState({ width: 0, height: 0 });

  const canvasRef = useRef<SignatureCanvas>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasAnyInk, setHasAnyInk] = useState(false);
  const [hasEnoughDrawing, setHasEnoughDrawing] = useState(false);

  const syncDrawingAmount = () => {
    const pad = canvasRef.current;
    setHasAnyInk(!!pad && !pad.isEmpty());
    setHasEnoughDrawing(hasReasonableSignature(pad));
  };

  const handleClear = () => {
    canvasRef.current?.clear();
    setHasAnyInk(false);
    setHasEnoughDrawing(false);
  };

  const { createSubmission } = useCreateSubmission<RaffleSubmissionData>(false);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) {
      return;
    }

    const updateSize = () => {
      const rect = overlay.getBoundingClientRect();
      const nextWidth = Math.round(rect.width);
      const nextHeight = Math.round(rect.height);
      setPadSize((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(overlay);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setHasAnyInk(false);
    setHasEnoughDrawing(false);
  }, [padSize.width, padSize.height]);

  useEffect(() => {
    if (skipIntro) {
      return;
    }
    const timeout = setTimeout(() => {
      setHasFlownIn(true);
    }, FLIGHT_WAIT_TIME);

    return () => clearTimeout(timeout);
  }, [skipIntro]);

  useEffect(() => {
    if (skipIntro) {
      return;
    }
    const timeout = setTimeout(() => {
      setShowSignWell(true);
    }, FLIP_WAIT_TIME);

    return () => clearTimeout(timeout);
  }, [skipIntro]);

  const handleSubmit = async () => {
    if (canvasRef.current?.isEmpty()) {
      toast.error('Sign the ticket first!');
      return;
    }

    if (!hasReasonableSignature(canvasRef.current)) {
      toast.error('Draw a bit more so we can read your signature');
      setHasEnoughDrawing(false);
      return;
    }

    if (!user) {
      toast.error('You must be logged in to submit a ticket');
      return;
    }

    setSubmitting(true);

    const signaturePad = canvasRef.current;
    const liveCanvas = signaturePad?.getCanvas();
    if (!signaturePad || !liveCanvas) {
      toast.error('Error creating signature file');
      setSubmitting(false);
      return;
    }

    const cssWidth = liveCanvas.offsetWidth || padSize.width;
    const cssHeight = liveCanvas.offsetHeight || padSize.height;
    if (cssWidth < 1 || cssHeight < 1) {
      toast.error('Error creating signature file');
      setSubmitting(false);
      return;
    }

    let canvasBlob: Blob;
    try {
      const pointGroups = signaturePad.toData() as SignaturePointGroup[];
      const exportCanvas = renderSignaturePointsToCanvas(
        pointGroups,
        cssWidth,
        cssHeight,
      );
      canvasBlob = await canvasToPngBlob(exportCanvas);
    } catch {
      toast.error('Error creating signature file');
      setSubmitting(false);
      return;
    }

    const canvasFile = new File([canvasBlob], 'signature.png', {
      type: 'image/png',
    });

    const submission = await createSubmission({
      engagementId,
      data: { name: user.name || user.id },
      uploads: [
        {
          field: 'optionalImageUrl',
          file: canvasFile,
          type: 'image',
        },
      ],
    });

    if ('errors' in submission) {
      toast.error('Error submitting ticket: ' + submission.errors.join(', '));
      setSubmitting(false);
      return;
    }

    onSubmitted(submission);

    toast.success('Raffle entered!');
  };

  return (
    <div
      data-name="RAFFLE_TICKET_CONTAINER"
      className={`relative flex flex-col gap-4 ${className ?? ''}`}
    >
      <div className="relative w-full" style={{ aspectRatio: '2' }}>
        <RaffleTicket
          visibleSide={skipIntro ? 'back' : 'front'}
          delayedFlip={skipIntro ? undefined : FLIP_WAIT_TIME}
          className={cn(
            'relative w-full',
            hasFlownIn
              ? 'top-0 scale-100 opacity-100'
              : '-top-[100vh] rotate-45 scale-[2] opacity-0',
            'transition-all duration-1000',
          )}
        />
        {showSignWell && !hasAnyInk && (
          <div
            aria-hidden
            data-name="RAFFLE_SIGN_HERE"
            className="border-black/32 pointer-events-none absolute inset-[21%_14%] z-[5] flex items-end justify-center rounded-lg border-[1.5px] border-dashed pb-2.5"
          >
            <span className="text-black/32 font-screen-body text-[13px] italic">
              Sign here
            </span>
          </div>
        )}
        <div
          ref={overlayRef}
          data-name="RAFFLE_TICKET_SIGNATURE_CANVAS"
          className="absolute inset-0 z-10"
        >
          {padSize.width > 0 && padSize.height > 0 && (
            <SignatureCanvas
              key={`${padSize.width}x${padSize.height}`}
              penColor="black"
              canvasProps={{
                className: 'block h-full w-full touch-none',
              }}
              backgroundColor="rgba(0,0,0,0)"
              ref={canvasRef}
              onEnd={syncDrawingAmount}
            />
          )}
        </div>
      </div>

      <div
        data-name="RAFFLE_TICKET_CTAS"
        className="relative z-20 mx-auto flex w-full max-w-[340px] flex-col items-center gap-2"
      >
        <div className="flex w-full justify-center gap-3">
          <Button
            size="xl"
            onClick={handleClear}
            className="h-12 flex-1 rounded-xl bg-white text-[16px] font-bold text-[#111] hover:bg-white/90"
          >
            Clear
          </Button>
          <Button
            size="xl"
            onClick={handleSubmit}
            disabled={submitting || !hasEnoughDrawing}
            title={
              hasEnoughDrawing ? undefined : 'Draw a bit more before submitting'
            }
            className={cn(
              'h-12 flex-1 rounded-xl text-[16px] font-bold text-white',
              hasEnoughDrawing
                ? 'bg-[#FF6B3D] shadow-[0_4px_18px_rgba(255,107,61,0.5)] hover:bg-[#FF6B3D]/90'
                : 'bg-[#FF6B3D]/40',
            )}
          >
            Submit
          </Button>
        </div>
        {!hasEnoughDrawing && hasAnyInk && (
          <div className="text-center font-screen-body text-sm">
            Draw a bit more before submitting
          </div>
        )}
      </div>
    </div>
  );
}
